import React from 'react'

import './uploadDeArquivosCard.css'

import FileService from '../../app/service/fileService'
import HandleErrorService from '../../app/service/handleErrorService'
import ProgressBarComponent from '../progress/progressBar'
import * as popUp from '../toastr'

/*
Card de upload reutilizável (versão "bonita" do FileUploadCard).
Cuida de tudo: arrastar/soltar, validação, envio pro backend e barra de progresso.

Props principais (mesmos nomes do FileUploadCard pra facilitar a troca):
    accept              -> extensões aceitas, ex: ".txt" ou ".zip, .xml, .pdf"
    multiple            -> permite mais de 1 arquivo (padrão: false)
    uploadDescription   -> tipoUpload que o backend espera (enum)
    extraObject         -> opcional, vai junto no form
    onFinish(chave)     -> chamado quando o backend termina de processar
    onFail()            -> chamado quando dá erro no envio ou no processamento
    uploadInProgress(b) -> avisa a tela se está enviando/processando

Props visuais (todas opcionais):
    titulo, subtitulo, icone
    tamanhoMaximoEmMB, textoDica, textoBotaoEnviar, textoProcessando
    successNoticeMessage, failNoticeMessage
    passosComoFunciona  -> lista de {titulo, texto}; se vier, mostra o painel lateral
*/

const tamanhoMaximoPadraoEmMB = 200

class UploadDeArquivosCard extends React.Component {

    constructor(){
        super()
        this.fileService = new FileService()
        this.inputDeArquivoEscondidoRef = React.createRef()
    }

    state = {
        arquivosSelecionados: [],
        arrastandoArquivoPorCima: false,

        // etapas possíveis: 'selecao' | 'processando' | 'concluido' | 'falha'
        etapaDoUpload: 'selecao',
        chaveDoProgresso: null,
    }

    // ---------- props com valor padrão ----------

    getTamanhoMaximoEmMB = () => {
        if(this.props.tamanhoMaximoEmMB){
            return this.props.tamanhoMaximoEmMB
        }
        return tamanhoMaximoPadraoEmMB
    }

    // transforma ".zip, .XML" em ['zip', 'xml']
    getListaDeExtensoesAceitas = () => {
        const listaDeExtensoesAceitas = []
        if(!this.props.accept){
            return listaDeExtensoesAceitas
        }

        const partesDoAccept = this.props.accept.split(',')
        for(const parte of partesDoAccept){
            let extensao = parte.trim().toLowerCase()
            if(extensao.startsWith('.')){
                extensao = extensao.substring(1)
            }
            if(extensao){
                listaDeExtensoesAceitas.push(extensao)
            }
        }
        return listaDeExtensoesAceitas
    }

    getTextoDasExtensoesAceitas = () => {
        const listaComPonto = []
        for(const extensao of this.getListaDeExtensoesAceitas()){
            listaComPonto.push('.' + extensao)
        }
        return listaComPonto.join(', ')
    }

    getTextoDica = () => {
        if(this.props.textoDica){
            return this.props.textoDica
        }
        const quantidade = this.props.multiple ? 'Um ou mais arquivos' : 'Apenas 1 arquivo'
        return quantidade + ' ' + this.getTextoDasExtensoesAceitas() + ' · até ' + this.getTamanhoMaximoEmMB() + 'MB'
    }

    // ---------- seleção dos arquivos ----------

    estaProcessando = () => {
        return this.state.etapaDoUpload === 'processando'
    }

    abrirSeletorDeArquivo = () => {
        if(this.estaProcessando()){
            return
        }
        this.inputDeArquivoEscondidoRef.current.click()
    }

    onArquivoEscolhidoNoInput = (event) => {
        this.validaESelecionaArquivos(event.target.files)

        // limpa o input pra permitir escolher o mesmo arquivo de novo depois
        event.target.value = ''
    }

    validaESelecionaArquivos = (listaDeArquivosNovos) => {
        if(!listaDeArquivosNovos || listaDeArquivosNovos.length === 0){
            return
        }

        if(!this.props.multiple && listaDeArquivosNovos.length > 1){
            popUp.infoPopUp("Só é permitido enviar 1 arquivo.")
            return
        }

        // confere a extensão de cada arquivo
        const listaDeExtensoesAceitas = this.getListaDeExtensoesAceitas()
        for(const arquivo of listaDeArquivosNovos){
            const extensaoDoArquivo = arquivo.name.split('.').pop().toLowerCase()
            if(!listaDeExtensoesAceitas.includes(extensaoDoArquivo)){
                popUp.warningPopUp("São aceitos apenas arquivos " + this.getTextoDasExtensoesAceitas())
                return
            }
        }

        // se aceita vários, soma com os que já estavam; senão, substitui
        let arquivosQueVaoFicar = []
        if(this.props.multiple){
            for(const arquivoJaSelecionado of this.state.arquivosSelecionados){
                arquivosQueVaoFicar.push(arquivoJaSelecionado)
            }
        }
        for(const arquivoNovo of listaDeArquivosNovos){
            // evita o mesmo arquivo duas vezes na lista
            let jaEstaNaLista = false
            for(const arquivoQueVaiFicar of arquivosQueVaoFicar){
                if(arquivoQueVaiFicar.name === arquivoNovo.name && arquivoQueVaiFicar.size === arquivoNovo.size){
                    jaEstaNaLista = true
                }
            }
            if(!jaEstaNaLista){
                arquivosQueVaoFicar.push(arquivoNovo)
            }
        }

        // confere o tamanho total
        let tamanhoTotalEmBytes = 0
        for(const arquivo of arquivosQueVaoFicar){
            tamanhoTotalEmBytes += arquivo.size
        }
        const tamanhoTotalEmMB = tamanhoTotalEmBytes / 1000000
        if(tamanhoTotalEmMB > this.getTamanhoMaximoEmMB()){
            popUp.warningPopUp("O tamanho total (" + tamanhoTotalEmMB.toFixed(1) + "MB) não pode ultrapassar " + this.getTamanhoMaximoEmMB() + "MB.")
            return
        }

        this.setState({arquivosSelecionados: arquivosQueVaoFicar})
        this.setState({etapaDoUpload: 'selecao'})
    }

    removerArquivoSelecionado = (indexDoArquivoParaRemover) => {
        const arquivosQueVaoFicar = []
        for(let i = 0; i < this.state.arquivosSelecionados.length; i++){
            if(i !== indexDoArquivoParaRemover){
                arquivosQueVaoFicar.push(this.state.arquivosSelecionados[i])
            }
        }
        this.setState({arquivosSelecionados: arquivosQueVaoFicar})
    }

    // ---------- drag and drop ----------

    onArrastarPorCima = (event) => {
        event.preventDefault()
        if(this.estaProcessando()){
            return
        }
        if(!this.state.arrastandoArquivoPorCima){
            this.setState({arrastandoArquivoPorCima: true})
        }
    }

    onSairDaAreaDeArrastar = (event) => {
        event.preventDefault()
        this.setState({arrastandoArquivoPorCima: false})
    }

    onSoltarArquivo = (event) => {
        event.preventDefault()
        this.setState({arrastandoArquivoPorCima: false})
        if(this.estaProcessando()){
            return
        }
        this.validaESelecionaArquivos(event.dataTransfer.files)
    }

    // ---------- upload ----------

    avisaTelaSeEstaEmAndamento = (estaEmAndamento) => {
        if(this.props.uploadInProgress){
            this.props.uploadInProgress(estaEmAndamento)
        }
    }

    enviarArquivos = () => {
        if(this.state.arquivosSelecionados.length === 0){
            return
        }

        // monta o form igual o FileUploadCard faz, pro backend reconhecer
        const bodyFormData = new FormData()
        for(const arquivo of this.state.arquivosSelecionados){
            bodyFormData.append('files', arquivo)
        }
        if(this.props.extraObject){
            bodyFormData.append('extraObject', this.props.extraObject)
        }
        bodyFormData.append('tipoUpload', this.props.uploadDescription)

        this.setState({etapaDoUpload: 'processando'})
        this.setState({chaveDoProgresso: null})
        this.avisaTelaSeEstaEmAndamento(true)

        this.fileService.mutipleUpload(bodyFormData)
        .then(response => {
            // com a chave em mãos o ProgressBarComponent começa a acompanhar o processamento
            this.setState({chaveDoProgresso: response.data})
        })
        .catch(error => {
            HandleErrorService.handleError(error)
            this.onFalhaNoProcessamento()
        })
    }

    onFimDoProcessamento = () => {
        this.setState({etapaDoUpload: 'concluido'})
        this.avisaTelaSeEstaEmAndamento(false)

        if(this.props.onFinish){
            this.props.onFinish(this.state.chaveDoProgresso)
        }
    }

    onFalhaNoProcessamento = () => {
        this.setState({etapaDoUpload: 'falha'})
        this.avisaTelaSeEstaEmAndamento(false)

        if(this.props.onFail){
            this.props.onFail()
        }
    }

    recomecarUpload = () => {
        this.setState({arquivosSelecionados: []})
        this.setState({chaveDoProgresso: null})
        this.setState({etapaDoUpload: 'selecao'})
    }

    // ---------- helpers de exibição ----------

    formataTamanhoDoArquivo = (tamanhoEmBytes) => {
        if(tamanhoEmBytes < 1000){
            return tamanhoEmBytes + ' B'
        }
        if(tamanhoEmBytes < 1000000){
            return (tamanhoEmBytes / 1000).toFixed(1) + ' KB'
        }
        return (tamanhoEmBytes / 1000000).toFixed(1) + ' MB'
    }

    render() {

        const estaProcessando = this.estaProcessando()

        // cabeçalho só aparece se a tela passar um título
        const renderCabecalho = () => {
            if(!this.props.titulo){
                return null
            }

            return (
                <div className="uda-header">
                    <div className="uda-header-icone">
                        <i className={this.props.icone ? this.props.icone : 'pi pi-upload'} />
                    </div>
                    <div>
                        <div className="uda-titulo">{this.props.titulo}</div>
                        {this.props.subtitulo &&
                            <div className="uda-subtitulo">{this.props.subtitulo}</div>
                        }
                    </div>
                </div>
            )
        }

        // área pontilhada de arrastar/clicar
        const renderAreaDeUpload = () => {
            let classeDaArea = 'uda-dropzone'
            if(this.state.arrastandoArquivoPorCima){
                classeDaArea += ' uda-dropzone-ativa'
            }
            if(estaProcessando){
                classeDaArea += ' uda-dropzone-desabilitada'
            }

            let textoDoTitulo = this.props.multiple ? 'Arraste os arquivos até aqui' : 'Arraste o arquivo até aqui'
            if(this.state.arrastandoArquivoPorCima){
                textoDoTitulo = 'Solte aqui'
            }

            return (
                <div
                    className={classeDaArea}
                    onClick={this.abrirSeletorDeArquivo}
                    onDragOver={this.onArrastarPorCima}
                    onDragLeave={this.onSairDaAreaDeArrastar}
                    onDrop={this.onSoltarArquivo}
                >
                    <div className="uda-dropzone-icone">
                        <i className="pi pi-cloud-upload" />
                    </div>
                    <div className="uda-dropzone-titulo">{textoDoTitulo}</div>
                    <div className="uda-dropzone-subtitulo">
                        ou <span className="uda-link">clique para escolher</span> no computador
                    </div>
                    <div className="uda-dropzone-dica">{this.getTextoDica()}</div>

                    <input
                        ref={this.inputDeArquivoEscondidoRef}
                        type="file"
                        accept={this.props.accept}
                        multiple={this.props.multiple ? true : false}
                        style={{display: 'none'}}
                        onChange={this.onArquivoEscolhidoNoInput}
                    />
                </div>
            )
        }

        // cartãozinho de cada arquivo escolhido
        const renderArquivosSelecionados = () => {
            if(this.state.arquivosSelecionados.length === 0){
                return null
            }

            const cartoesDosArquivos = []
            for(let i = 0; i < this.state.arquivosSelecionados.length; i++){
                const arquivo = this.state.arquivosSelecionados[i]
                cartoesDosArquivos.push(
                    <div className="uda-arquivo" key={arquivo.name + '_' + i}>
                        <div className="uda-arquivo-icone">
                            <i className="pi pi-file" />
                        </div>
                        <div className="uda-arquivo-info">
                            <div className="uda-arquivo-nome" title={arquivo.name}>{arquivo.name}</div>
                            <div className="uda-arquivo-tamanho">{this.formataTamanhoDoArquivo(arquivo.size)}</div>
                        </div>
                        {estaProcessando ? null : (
                            <div role="button" tabIndex={0} className="uda-arquivo-remover" onClick={() => this.removerArquivoSelecionado(i)} title="Remover arquivo">
                                <i className="pi pi-times" />
                            </div>
                        )}
                    </div>
                )
            }

            return <div className="uda-lista-arquivos">{cartoesDosArquivos}</div>
        }

        // barra de progresso do backend (só aparece depois que o upload devolve a chave)
        const renderProgresso = () => {
            if(estaProcessando && !this.state.chaveDoProgresso){
                return (
                    <div className="uda-status uda-status-neutro">
                        <i className="pi pi-spin pi-spinner" />
                        <span>Enviando...</span>
                    </div>
                )
            }

            if(this.state.chaveDoProgresso){
                return (
                    <div className="uda-progresso">
                        <div className="uda-progresso-label">
                            {this.props.textoProcessando ? this.props.textoProcessando : 'Processando'}
                        </div>
                        <ProgressBarComponent
                            progressKey={this.state.chaveDoProgresso}
                            endProgress={this.onFimDoProcessamento}
                            failProgress={this.onFalhaNoProcessamento}
                            successNoticeMessage={this.props.successNoticeMessage ? this.props.successNoticeMessage : 'Upload concluído com sucesso!'}
                            failNoticeMessage={this.props.failNoticeMessage ? this.props.failNoticeMessage : 'Falha no upload. Tente novamente.'}
                        />
                    </div>
                )
            }

            if(this.state.etapaDoUpload === 'falha'){
                return (
                    <div className="uda-status uda-status-erro">
                        <i className="pi pi-exclamation-circle" />
                        <span>Não foi possível enviar. Tente novamente.</span>
                    </div>
                )
            }

            return null
        }

        // botões do rodapé mudam conforme a etapa
        const renderAcoes = () => {
            if(this.state.etapaDoUpload === 'concluido' || this.state.etapaDoUpload === 'falha'){
                return (
                    <div className="uda-acoes">
                        <div role="button" tabIndex={0} className="uda-botao uda-botao-primario" onClick={this.recomecarUpload}>
                            <i className="pi pi-refresh" />
                            <span>Enviar outro arquivo</span>
                        </div>
                    </div>
                )
            }

            const botaoEnviarDesabilitado = this.state.arquivosSelecionados.length === 0 || estaProcessando
            let classeDoBotaoEnviar = 'uda-botao uda-botao-primario'
            if(botaoEnviarDesabilitado){
                classeDoBotaoEnviar += ' uda-botao-desabilitado'
            }

            let textoDoBotaoEnviar = this.props.textoBotaoEnviar ? this.props.textoBotaoEnviar : 'Enviar'
            if(estaProcessando){
                textoDoBotaoEnviar = 'Enviando...'
            }

            return (
                <div className="uda-acoes">
                    <div role="button" tabIndex={0}
                        className={classeDoBotaoEnviar}
                        onClick={botaoEnviarDesabilitado ? undefined : this.enviarArquivos}
                    >
                        {estaProcessando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-upload" />}
                        <span>{textoDoBotaoEnviar}</span>
                    </div>
                </div>
            )
        }

        // painel lateral "como funciona" (só se a tela passar os passos)
        const renderComoFunciona = () => {
            if(!this.props.passosComoFunciona || this.props.passosComoFunciona.length === 0){
                return null
            }

            const itensDosPassos = []
            for(let i = 0; i < this.props.passosComoFunciona.length; i++){
                const passo = this.props.passosComoFunciona[i]
                itensDosPassos.push(
                    <li key={i}>
                        <span className="uda-passo-numero">{i + 1}</span>
                        <div>
                            <div className="uda-passo-titulo">{passo.titulo}</div>
                            <div className="uda-passo-texto">{passo.texto}</div>
                        </div>
                    </li>
                )
            }

            return (
                <aside className="uda-lateral">
                    <div className="uda-lateral-titulo">Como funciona</div>
                    <ol className="uda-passos">{itensDosPassos}</ol>
                </aside>
            )
        }

        return (
            <div className="uda-card">
                {renderCabecalho()}

                <div className="uda-corpo">
                    <div className="uda-principal">
                        {renderAreaDeUpload()}
                        {renderArquivosSelecionados()}
                        {renderProgresso()}
                        {renderAcoes()}
                    </div>

                    {renderComoFunciona()}
                </div>
            </div>
        )
    }

}

export default UploadDeArquivosCard
